import {
  Engine,
  KeyEvent,
  Keys,
  PointerEvent,
  PointerType,
  Random,
  Scene,
  SceneActivationContext,
  vec,
} from 'excalibur';
import { ClockFace } from '../actors/clock-face';
import { ClockHand } from '../actors/clock-hand';
import { ClockHub } from '../actors/clock-hub';
import { Marker } from '../actors/marker';
import { PaperBackground } from '../actors/paper-background';
import { MONETIZATION, isMonetizationActive } from '../monetization/config';
import { incrementRunCount } from '../monetization/entitlements';
import { refreshSceneTheme } from '../themes/theme';
import { ComboMeter } from '../ui/combo-meter';
import { Hud } from '../ui/hud';
import { SideToggle } from '../ui/side-toggle';
import { SpeedButton } from '../ui/speed-button';
import { degreesBetween, normalizeDegrees, randomAngleAwayFrom, toRadians } from '../utils/angles';
import { sfx } from '../utils/audio';
import { haptics } from '../utils/haptics';
import {
  BOOST_MULTIPLIER,
  CLOCK_CENTER_X,
  CLOCK_CENTER_Y,
  CLOCK_SHAKE_DURATION,
  CLOCK_SHAKE_MAGNITUDE,
  COMBO_FIRE_MULTIPLIER,
  COMBO_HITS_PER_STEP,
  COMBO_MAX_MULTIPLIER,
  DESIGN_HEIGHT,
  DESIGN_WIDTH,
  DOUBLES_PER_HEART,
  DOUBLE_HIT_BONUS,
  HIT_TIME_BONUS,
  HIT_WINDOW_DEG,
  HOUR_BASE_SPEED,
  HOUR_HAND_LENGTH,
  HOUR_HAND_THICKNESS,
  MARKER_MIN_FROM_HAND,
  MARKER_MIN_FROM_OTHER,
  MARKER_RADIUS,
  MAX_FRAME_DELTA_MS,
  MINUTE_BASE_SPEED,
  MINUTE_HAND_LENGTH,
  MINUTE_HAND_THICKNESS,
  RIM_RADIUS,
  SEEDS,
  SINGLE_HIT_POINTS,
  START_LIVES,
  START_TIME,
  STUTTER_DURATION,
  STUTTER_MULTIPLIER,
  STUTTER_WOBBLE_DEG,
} from '../utils/constants';
import { Palette } from '../utils/palette';
import { Expression } from '../utils/sketch';
import { rememberCapture } from '../utils/share';
import { readBestScore, writeBestScore, ButtonSide, readSpeedButtonSide, writeSpeedButtonSide } from '../utils/storage';
import { EndReason, GameResult, ReviveState } from '../utils/types';
import { polarOffset } from '../utils/angles';

export interface GameDebugState {
  minuteAngle: number;
  hourAngle: number;
  minuteMarkerAngle: number;
  hourMarkerAngle: number;
  score: number;
  lives: number;
  time: number;
  hits: number;
  doubles: number;
  combo: number;
  bestCombo: number;
  ended: boolean;
}

export class GameScene extends Scene<ReviveState | undefined> {
  private clock!: ClockFace;
  private minuteHand!: ClockHand;
  private hourHand!: ClockHand;
  private minuteMarker!: Marker;
  private hourMarker!: Marker;
  private hud!: Hud;
  private speedButton!: SpeedButton;
  private sideToggle!: SideToggle;
  private comboMeter!: ComboMeter;
  private readonly rng = new Random(SEEDS.game);
  private readonly clockHome = vec(CLOCK_CENTER_X, CLOCK_CENTER_Y);
  private elapsed = 0;
  private revivesUsed = 0;
  private score = 0;
  private lives = START_LIVES;
  private timeLeft = START_TIME;
  private hits = 0;
  private doubles = 0;
  private combo = 0;
  private bestCombo = 0;
  private stutterTimer = 0;
  private shakeTimer = 0;
  private graceTimer = 1.1;
  private boostButtonHold = false;
  private boostButtonLock = false;
  private boostPointerId: number | null = null;
  private speedSide: ButtonSide = 'left';
  private togglePressTimer = 0;
  private ended = false;

  onInitialize(): void {
    this.add(new PaperBackground());

    this.clock = new ClockFace();
    this.add(this.clock);

    this.hourMarker = new Marker({
      name: 'hour-marker',
      tone: 'pink',
      deepTone: 'pinkDeep',
      guideLength: 78,
      seed: SEEDS.hourMarker,
    });
    this.minuteMarker = new Marker({
      name: 'minute-marker',
      tone: 'blue',
      deepTone: 'blueDeep',
      guideLength: 26,
      seed: SEEDS.minuteMarker,
    });
    this.hourHand = new ClockHand({
      name: 'hour-hand',
      length: HOUR_HAND_LENGTH,
      thickness: HOUR_HAND_THICKNESS,
      bodyTone: 'pink',
      edgeTone: 'pinkDeep',
      direction: -1,
      baseSpeed: HOUR_BASE_SPEED,
      seed: SEEDS.hourHand,
    });
    this.minuteHand = new ClockHand({
      name: 'minute-hand',
      length: MINUTE_HAND_LENGTH,
      thickness: MINUTE_HAND_THICKNESS,
      bodyTone: 'blue',
      edgeTone: 'blueDeep',
      direction: 1,
      baseSpeed: MINUTE_BASE_SPEED,
      seed: SEEDS.minuteHand,
    });
    const hub = new ClockHub();

    this.hourMarker.z = 6;
    this.minuteMarker.z = 7;
    this.hourHand.z = 10;
    this.minuteHand.z = 12;
    hub.z = 20;

    this.clock.addChild(this.hourMarker);
    this.clock.addChild(this.minuteMarker);
    this.clock.addChild(this.hourHand);
    this.clock.addChild(this.minuteHand);
    this.clock.addChild(hub);

    this.hud = new Hud(this);

    this.comboMeter = new ComboMeter();
    this.add(this.comboMeter);

    this.speedButton = new SpeedButton({
      name: 'speed-button',
      pos: vec(DESIGN_WIDTH - 122, DESIGN_HEIGHT - 160),
      radius: 64,
    });
    this.speedButton.z = 40;
    this.add(this.speedButton);

    this.sideToggle = new SideToggle({
      name: 'side-toggle',
      pos: vec(250, DESIGN_HEIGHT - 166),
      radius: 30,
    });
    this.sideToggle.z = 40;
    this.add(this.sideToggle);

    this.applySpeedSide(readSpeedButtonSide());
  }

  private applySpeedSide(side: ButtonSide): void {
    this.speedSide = side;
    const buttonX = side === 'left' ? 116 : DESIGN_WIDTH - 116;
    const toggleX = side === 'left' ? 250 : DESIGN_WIDTH - 250;
    const buttonY = DESIGN_HEIGHT - 160;
    this.speedButton.pos = vec(buttonX, buttonY);
    this.sideToggle.pos = vec(toggleX, buttonY - 6);
  }

  private flipSpeedSide(): void {
    const next: ButtonSide = this.speedSide === 'left' ? 'right' : 'left';
    writeSpeedButtonSide(next);
    this.applySpeedSide(next);
    this.togglePressTimer = 0.2;
    this.sideToggle.setPressed(true);
    sfx.start();
    this.hud.popup(
      next === 'left' ? 'button on the left' : 'button on the right',
      vec(CLOCK_CENTER_X, DESIGN_HEIGHT - 320),
      Palette.inkSoft,
      30,
      50,
    );
  }

  onActivate(context: SceneActivationContext<ReviveState | undefined>): void {
    refreshSceneTheme(this);
    this.resetRun(context.data);
    this.input.pointers.on('down', this.handlePointerDown);
    this.input.pointers.on('up', this.handlePointerUp);
    this.input.keyboard.on('press', this.handleKeyPress);
    window.addEventListener('blur', this.handleWindowBlur);
  }

  onDeactivate(): void {
    this.input.pointers.off('down', this.handlePointerDown);
    this.input.pointers.off('up', this.handlePointerUp);
    this.input.keyboard.off('press', this.handleKeyPress);
    window.removeEventListener('blur', this.handleWindowBlur);
  }

  onPreUpdate(_engine: Engine, elapsed: number): void {
    const dt = Math.min(elapsed, MAX_FRAME_DELTA_MS) / 1000;
    if (this.ended) {
      return;
    }
    this.elapsed += dt;

    if (this.graceTimer > 0) {
      this.graceTimer -= dt;
      if (this.graceTimer <= 0) {
        this.graceTimer = 0;
        this.hud.setReady(false);
      }
    }

    if (this.stutterTimer > 0) {
      this.stutterTimer = Math.max(0, this.stutterTimer - dt);
    }

    const boosting = this.boostButtonHold || this.boostButtonLock || this.isBoostHeld();
    this.speedButton.setPressed(boosting);
    if (this.togglePressTimer > 0) {
      this.togglePressTimer = Math.max(0, this.togglePressTimer - dt);
      if (this.togglePressTimer === 0) {
        this.sideToggle.setPressed(false);
      }
    }
    const multiplier =
      (boosting ? BOOST_MULTIPLIER : 1) *
      (this.stutterTimer > 0 ? STUTTER_MULTIPLIER : 1) *
      (this.graceTimer > 0 ? 0.4 : 1);

    this.minuteHand.spin(dt, multiplier);
    this.hourHand.spin(dt, multiplier);

    if (this.stutterTimer > 0) {
      const wobble = Math.sin(this.elapsed * 46) * STUTTER_WOBBLE_DEG;
      this.minuteHand.addWobble(wobble);
      this.hourHand.addWobble(-wobble);
    }

    this.updateFeedback();
    this.updateShake(dt);

    if (this.graceTimer <= 0) {
      this.timeLeft -= dt;
      if (this.timeLeft <= 0) {
        this.timeLeft = 0;
        this.hud.setTime(0);
        void this.endRun('time');
        return;
      }
    }

    this.hud.setTime(this.timeLeft);
    this.hud.update(dt);
  }

  debugState(): GameDebugState {
    return {
      minuteAngle: this.minuteHand.angleDeg,
      hourAngle: this.hourHand.angleDeg,
      minuteMarkerAngle: this.minuteMarker.angleDeg,
      hourMarkerAngle: this.hourMarker.angleDeg,
      score: this.score,
      lives: this.lives,
      time: this.timeLeft,
      hits: this.hits,
      doubles: this.doubles,
      combo: this.combo,
      bestCombo: this.bestCombo,
      ended: this.ended,
    };
  }

  private resetRun(carry?: ReviveState): void {
    this.score = carry?.score ?? 0;
    this.hits = carry?.hits ?? 0;
    this.doubles = carry?.doubles ?? 0;
    this.bestCombo = carry?.bestCombo ?? 0;
    this.combo = 0;
    this.revivesUsed = carry?.revives ?? 0;
    this.lives = carry ? 1 : START_LIVES;
    this.timeLeft = carry ? MONETIZATION.revive.timeSeconds : START_TIME;
    this.stutterTimer = 0;
    this.shakeTimer = 0;
    this.graceTimer = carry ? 1.0 : 1.1;
    this.boostButtonHold = false;
    this.boostButtonLock = false;
    this.boostPointerId = null;
    this.ended = false;
    this.elapsed = 0;
    this.speedButton.setPressed(false);

    this.clock.pos = vec(this.clockHome.x, this.clockHome.y);
    this.clock.setBaseExpression('idle');
    this.clock.setExpression('idle');
    this.comboMeter.reset();

    this.minuteHand.reset();
    this.hourHand.reset();
    this.minuteHand.angleDeg = this.rng.floating(0, 360);
    this.hourHand.angleDeg = normalizeDegrees(
      this.minuteHand.angleDeg + 110 + this.rng.floating(0, 140),
    );
    this.minuteHand.rotation = toRadians(this.minuteHand.angleDeg);
    this.hourHand.rotation = toRadians(this.hourHand.angleDeg);

    this.minuteMarker.setAngle(this.rng.floating(0, 360));
    this.hourMarker.setAngle(this.rng.floating(0, 360));
    this.respawnMarker(this.minuteMarker, this.minuteHand, this.hourMarker, true);
    this.respawnMarker(this.hourMarker, this.hourHand, this.minuteMarker, true);

    this.hud.reset();
    this.hud.setScore(this.score);
    this.hud.setLives(this.lives);
    this.hud.setDoubles(this.doubles);
    this.hud.setTime(this.timeLeft);
    this.hud.setReady(true);
  }

  private isInWindow(hand: ClockHand, marker: Marker): boolean {
    return degreesBetween(hand.angleDeg, marker.angleDeg) <= HIT_WINDOW_DEG;
  }

  private isBoostHeld(): boolean {
    const keyboard = this.engine.input.keyboard;
    return (
      keyboard.isHeld(Keys.ShiftLeft) || keyboard.isHeld(Keys.ShiftRight) || keyboard.isHeld(Keys.Space)
    );
  }

  private updateFeedback(): void {
    const minuteHot = this.isInWindow(this.minuteHand, this.minuteMarker);
    const hourHot = this.isInWindow(this.hourHand, this.hourMarker);
    this.minuteMarker.setHot(minuteHot);
    this.hourMarker.setHot(hourHot);
    this.minuteHand.setHot(minuteHot);
    this.hourHand.setHot(hourHot);
  }

  private updateShake(dt: number): void {
    if (this.shakeTimer > 0) {
      this.shakeTimer = Math.max(0, this.shakeTimer - dt);
      const progress = this.shakeTimer / CLOCK_SHAKE_DURATION;
      const magnitude = CLOCK_SHAKE_MAGNITUDE * progress;
      this.clock.pos = vec(
        this.clockHome.x + Math.sin(this.elapsed * 52) * magnitude,
        this.clockHome.y + Math.cos(this.elapsed * 61) * magnitude * 0.7,
      );
    } else {
      this.clock.pos = vec(this.clockHome.x, this.clockHome.y);
    }
  }

  private tap(): void {
    if (this.ended || this.graceTimer > 0) {
      return;
    }
    const minuteHit = this.isInWindow(this.minuteHand, this.minuteMarker);
    const hourHit = this.isInWindow(this.hourHand, this.hourMarker);
    if (!minuteHit && !hourHit) {
      this.registerMiss();
      return;
    }

    const multiplier = this.multiplier;
    let gained = 0;
    if (minuteHit) {
      this.registerHandHit(this.minuteHand, this.minuteMarker, this.hourMarker, multiplier);
      gained += SINGLE_HIT_POINTS;
    }
    if (hourHit) {
      this.registerHandHit(this.hourHand, this.hourMarker, this.minuteMarker, multiplier);
      gained += SINGLE_HIT_POINTS;
    }

    if (minuteHit && hourHit) {
      gained += DOUBLE_HIT_BONUS;
      this.doubles += 1;
      this.hud.setDoubles(this.doubles);
      const doublePoints = (SINGLE_HIT_POINTS * 2 + DOUBLE_HIT_BONUS) * multiplier;
      this.hud.popup(
        multiplier > 1 ? `double! +${doublePoints}` : 'double! +300',
        vec(CLOCK_CENTER_X, CLOCK_CENTER_Y - RIM_RADIUS - 8),
        Palette.yellowDeep,
        54,
        100,
      );
      if (this.doubles % DOUBLES_PER_HEART === 0 && this.lives < START_LIVES) {
        this.lives += 1;
        this.hud.setLives(this.lives);
        this.hud.popup(
          '+1 heart!',
          vec(CLOCK_CENTER_X, CLOCK_CENTER_Y - RIM_RADIUS - 54),
          Palette.heartDeep,
          50,
          90,
        );
        sfx.doodle();
        haptics.heart();
      }
      sfx.doubleHit();
      haptics.double();
    } else {
      sfx.hit();
      haptics.hit();
    }

    this.combo += 1;
    this.bestCombo = Math.max(this.bestCombo, this.combo);
    const newMultiplier = this.multiplier;
    this.comboMeter.setMultiplier(newMultiplier);
    if (newMultiplier > multiplier) {
      if (newMultiplier >= COMBO_FIRE_MULTIPLIER) {
        sfx.doodle();
        haptics.fire();
        this.hud.popup(
          `combo x${newMultiplier}!`,
          vec(CLOCK_CENTER_X, CLOCK_CENTER_Y - RIM_RADIUS - 96),
          Palette.flameDeep,
          46,
          90,
        );
      } else {
        this.hud.popup(
          `combo x${newMultiplier}`,
          vec(CLOCK_CENTER_X, CLOCK_CENTER_Y - RIM_RADIUS - 96),
          Palette.blueDeep,
          40,
          70,
        );
      }
    }

    this.timeLeft += HIT_TIME_BONUS;
    this.addScore(gained * multiplier);
    const hitFaces: Expression[] =
      newMultiplier >= COMBO_FIRE_MULTIPLIER
        ? ['wow', 'grit', 'fire', 'grin']
        : ['happy', 'wow', 'grin', 'grit'];
    this.clock.setExpression(this.rng.pickOne(hitFaces));
  }

  private get multiplier(): number {
    return Math.min(COMBO_MAX_MULTIPLIER, 1 + Math.floor(this.combo / COMBO_HITS_PER_STEP));
  }

  private registerHandHit(hand: ClockHand, marker: Marker, other: Marker, multiplier: number): void {
    const popupPos = vec(this.clockHome.x, this.clockHome.y).add(
      polarOffset(MARKER_RADIUS, marker.angleDeg),
    );
    hand.grow();
    marker.flash();
    this.hits += 1;
    this.hud.popup(
      `+${SINGLE_HIT_POINTS * multiplier}`,
      popupPos,
      marker === this.minuteMarker ? Palette.blueDeep : Palette.pinkDeep,
      46,
    );
    this.respawnMarker(marker, hand, other, false);
  }

  private registerMiss(): void {
    this.lives -= 1;
    this.combo = 0;
    this.comboMeter.setMultiplier(1);
    this.hud.setLives(this.lives);
    this.hud.popup(
      'miss!',
      vec(CLOCK_CENTER_X, CLOCK_CENTER_Y - RIM_RADIUS - 22),
      Palette.pinkDeep,
      46,
      70,
    );
    sfx.miss();
    haptics.miss();
    this.stutterTimer = STUTTER_DURATION;
    this.shakeTimer = CLOCK_SHAKE_DURATION;
    const missFaces: Expression[] = ['sad', 'dizzy'];
    this.clock.setExpression(this.rng.pickOne(missFaces));
    if (this.lives <= 0) {
      void this.endRun('lives');
    }
  }

  private addScore(points: number): void {
    this.score += points;
    this.hud.setScore(this.score);
  }

  private respawnMarker(marker: Marker, hand: ClockHand, other: Marker, initial: boolean): void {
    let angle = randomAngleAwayFrom(this.rng, hand.angleDeg, MARKER_MIN_FROM_HAND);
    for (let attempt = 0; attempt < 24; attempt++) {
      const candidate = randomAngleAwayFrom(this.rng, hand.angleDeg, MARKER_MIN_FROM_HAND);
      angle = candidate;
      if (degreesBetween(candidate, other.angleDeg) >= MARKER_MIN_FROM_OTHER) {
        break;
      }
    }
    marker.setAngle(angle);
    if (!initial) {
      marker.flash();
    }
  }

  private async endRun(reason: EndReason): Promise<void> {
    if (this.ended) {
      return;
    }
    this.ended = true;
    haptics.gameOver();
    const previousBest = readBestScore();
    const newBest = this.score > previousBest;
    if (newBest) {
      writeBestScore(this.score);
    }
    const result: GameResult = {
      score: this.score,
      hits: this.hits,
      doubles: this.doubles,
      bestCombo: this.bestCombo,
      revives: this.revivesUsed,
      best: Math.max(previousBest, this.score),
      newBest,
      reason,
    };
    if (isMonetizationActive()) {
      incrementRunCount();
    }
    const capture = await this.captureFrame();
    if (capture) {
      rememberCapture(capture);
    }
    await this.engine.goToScene('gameover', { sceneActivationData: result });
  }

  private async captureFrame(): Promise<HTMLImageElement | null> {
    try {
      return await this.engine.screenshot(true);
    } catch {
      return null;
    }
  }

  private handlePointerDown = (evt: PointerEvent): void => {
    sfx.resume();
    if (this.sideToggle.isPointInside(evt.worldPos.x, evt.worldPos.y)) {
      this.flipSpeedSide();
      return;
    }
    if (this.speedButton.isPointInside(evt.worldPos.x, evt.worldPos.y)) {
      if (evt.pointerType === PointerType.Touch) {
        this.boostButtonHold = true;
        this.boostPointerId = evt.pointerId;
      } else {
        this.boostButtonLock = !this.boostButtonLock;
      }
      haptics.boost();
      return;
    }
    this.tap();
  };

  private handlePointerUp = (evt: PointerEvent): void => {
    if (this.boostPointerId !== null && evt.pointerId === this.boostPointerId) {
      this.boostButtonHold = false;
      this.boostPointerId = null;
    }
  };

  private handleWindowBlur = (): void => {
    this.boostButtonHold = false;
    this.boostButtonLock = false;
    this.boostPointerId = null;
  };

  private handleKeyPress = (evt: KeyEvent): void => {
    if (evt.key === Keys.Space) {
      evt.originalEvent?.preventDefault();
      return;
    }
    if (evt.key === Keys.Enter) {
      this.tap();
    }
  };
}
